import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useRoutePreloader } from '../routing/RouteManager';
import { getIcon } from '../utils/icons';
import { NAVIGATION_CONFIG, MOBILE_NAVIGATION_CONFIG, MORE_MENU_ITEMS } from '../utils/constants';
import { useRole } from '../../contexts/RoleContext';

interface NavigationItem {
  title: string;
  route: string;
  iconName: string;
}

const ResponsiveNavigation = () => {
  const location = useLocation();
  const { handleMouseEnter, handleFocus } = useRoutePreloader();
  const { canAccessPage } = useRole();
  const [isMobile, setIsMobile] = useState(false);
  
  const MoreHorizontalIcon = getIcon('MoreVertical');

  // Detectar se é mobile baseado no tamanho da tela
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint do Tailwind
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Renderizar item de navegação padrão
  const renderNavItem = (item: NavigationItem, className = "") => {
    const { title, route, iconName } = item;
    
    // Verificar se o usuário pode acessar esta página
    if (!canAccessPage(route)) {
      return null;
    }
    
    const isActive = location.pathname === route;
    const Icon = getIcon(iconName);
    
    return (
      <Link
        key={route}
        to={route}
        className={`flex flex-col items-center py-1 px-2 sm:py-2 sm:px-3 rounded-lg transition-all duration-200 min-w-0 ${
          isActive ? "text-emerald-600 bg-emerald-50" : "text-gray-500 hover:text-emerald-600 hover:bg-emerald-50"
        } ${className}`}
        onMouseEnter={() => handleMouseEnter(route)}
        onFocus={() => handleFocus(route)}
        rel="prefetch"
        data-preload={route}
        aria-label={`Navegar para ${title}`}
      >
        <Icon className="w-5 h-5 mb-1 flex-shrink-0" />
        <span className="text-xs font-medium truncate text-center">{title}</span>
      </Link>
    );
  };

  // Renderizar menu "Mais" para mobile
  const renderMoreMenu = () => {
    // Filtrar itens que o usuário pode acessar
    const accessibleItems = MORE_MENU_ITEMS.filter(item => canAccessPage(item.route));
    
    // Se não há itens acessíveis, não renderizar o menu
    if (accessibleItems.length === 0) {
      return null;
    }
    
    const hasActiveItem = accessibleItems.some(item => 
      location.pathname === item.route
    );

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={`flex flex-col items-center py-1 px-2 sm:py-2 sm:px-3 rounded-lg transition-all duration-200 min-w-0 h-auto ${
              hasActiveItem ? "text-emerald-600 bg-emerald-50" : "text-gray-500 hover:text-emerald-600 hover:bg-emerald-50"
            }`}
            aria-label="Mais opções"
          >
            <MoreHorizontalIcon className="w-5 h-5 mb-1 flex-shrink-0" />
            <span className="text-xs font-medium">Mais</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          side="top"
          className="mb-2 bg-white border border-gray-200 shadow-lg"
        >
          {accessibleItems.map(item => {
            const { title, route, iconName } = item;
            const isActive = location.pathname === route;
            const Icon = getIcon(iconName);
            
            return (
              <DropdownMenuItem key={route} asChild>
                <Link
                  to={route}
                  className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
                    isActive ? 'bg-emerald-50 text-emerald-700' : 'hover:bg-gray-50'
                  }`}
                  onMouseEnter={() => handleMouseEnter(route)}
                  onFocus={() => handleFocus(route)}
                  rel="prefetch"
                  aria-label={`Navegar para ${title}`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="font-medium">{title}</span>
                </Link>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <nav 
      className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40"
      style={{ willChange: 'transform' }}
      data-component="Navigation"
    >
      <div className={`flex items-center justify-around py-2 mx-auto ${
        isMobile ? 'max-w-sm' : 'max-w-2xl'
      }`}>
        {isMobile ? (
          <>
            {/* Mobile: Exibir apenas primeiros 3 itens + menu Mais */}
            {MOBILE_NAVIGATION_CONFIG.filter(item => canAccessPage(item.route)).map(item => renderNavItem(item))}
            {renderMoreMenu()}
          </>
        ) : (
          <>
            {/* Desktop: Exibir todos os 5 itens */}
            {NAVIGATION_CONFIG.filter(item => canAccessPage(item.route)).map(item => renderNavItem(item, 'flex-1 max-w-32'))}
          </>
        )}
      </div>
    </nav>
  );
};

export default ResponsiveNavigation;
